import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STORAGE_KEY = 'mb_onboarded';

interface OnboardingSheetProps {
  onComplete: () => void;
  show: boolean;
}

export default function OnboardingSheet({ show, onComplete }: OnboardingSheetProps) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [show]);

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    setTimeout(onComplete, 350);
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    setTimeout(onComplete, 350);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-x-0 bottom-0 z-50 flex justify-center pointer-events-none"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28, duration: 0.4 }}
        >
          <div
            className="w-full max-w-[420px] pointer-events-auto"
            style={{
              background: 'hsl(var(--background) / 0.97)',
              backdropFilter: 'blur(20px)',
              borderTop: '1px solid hsl(var(--foreground) / 0.08)',
              borderRadius: '24px 24px 0 0',
              padding: '24px 20px 36px',
            }}
          >
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mb-5">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === step ? 10 : 6,
                    height: 6,
                    background: i === step
                      ? 'hsl(var(--bitcoin) / 0.7)'
                      : 'hsl(var(--foreground) / 0.1)',
                    borderRadius: i === step ? 3 : '50%',
                  }}
                />
              ))}
            </div>

            {/* Skip */}
            <button
              onClick={handleSkip}
              className="absolute top-5 right-5 text-[8px] font-body tracking-wider uppercase transition-colors"
              style={{ color: 'hsl(var(--foreground) / 0.2)' }}
            >
              skip
            </button>

            {/* Step content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {step === 0 && <Step1 />}
                {step === 1 && <Step2 />}
                {step === 2 && <Step3 />}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              {step > 0 ? (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="text-[9px] font-body tracking-wider"
                  style={{ color: 'hsl(var(--foreground) / 0.25)' }}
                >
                  ←
                </button>
              ) : <div />}

              <button
                onClick={() => step < 2 ? setStep(s => s + 1) : handleComplete()}
                className="rounded-xl px-6 py-2.5 text-[11px] font-body font-medium transition-colors"
                style={{
                  background: 'hsl(var(--bitcoin) / 0.15)',
                  border: '1px solid hsl(var(--bitcoin) / 0.25)',
                  color: 'hsl(var(--bitcoin))',
                }}
              >
                {step === 0 ? 'Say hi →' : step === 1 ? 'Got it →' : "Let's go ✓"}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Step1() {
  return (
    <div className="text-center">
      <motion.div
        className="text-5xl mb-4"
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        ⛏️
      </motion.div>
      <h2
        className="font-pixel text-[13px] tracking-wider mb-3"
        style={{ color: 'hsl(var(--bitcoin))' }}
      >
        Meet Mempool Buddy
      </h2>
      <p
        className="font-body text-[11px] leading-relaxed mb-2"
        style={{ color: 'hsl(var(--foreground) / 0.7)' }}
      >
        A living creature that watches the Bitcoin network
        so you don't have to.
      </p>
      <p
        className="font-body text-[9px] leading-relaxed"
        style={{ color: 'hsl(var(--foreground) / 0.35)' }}
      >
        Buddy's mood, expressions, and environment all reflect
        what's actually happening on-chain right now.
      </p>
    </div>
  );
}

function Step2() {
  const faces = [
    { emoji: '😰', label: 'congested mempool' },
    { emoji: '😊', label: 'quiet network' },
    { emoji: '🚀', label: 'price pumping' },
  ];
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-6 mb-4">
        {faces.map(f => (
          <div key={f.label} className="flex flex-col items-center gap-1">
            <span className="text-[20px]">{f.emoji}</span>
            <span
              className="font-body text-[7px] leading-tight"
              style={{ color: 'hsl(var(--foreground) / 0.35)' }}
            >
              {f.label}
            </span>
          </div>
        ))}
      </div>
      <h2
        className="font-pixel text-[11px] tracking-wider mb-3"
        style={{ color: 'hsl(var(--bitcoin))' }}
      >
        Buddy reacts to the network
      </h2>
      <p
        className="font-body text-[11px] leading-relaxed"
        style={{ color: 'hsl(var(--foreground) / 0.7)' }}
      >
        Expressions, posture, and the island environment
        all change with mempool congestion, fees, price,
        and market sentiment.
      </p>
    </div>
  );
}

function Step3() {
  return (
    <div className="text-center">
      {/* Mini HUD mockup */}
      <div
        className="rounded-xl mx-auto mb-4 px-4 py-2.5 flex items-center justify-between max-w-[260px]"
        style={{
          background: 'hsl(var(--foreground) / 0.05)',
          border: '1px solid hsl(var(--foreground) / 0.06)',
        }}
      >
        <div className="text-center">
          <span className="font-body font-semibold text-[10px] block" style={{ color: 'hsl(var(--foreground) / 0.7)' }}>$97,250</span>
          <span className="font-body text-[6px]" style={{ color: 'hsl(var(--foreground) / 0.3)' }}>BTC price</span>
        </div>
        <div className="text-center">
          <span className="font-body font-semibold text-[10px] block" style={{ color: 'hsl(var(--foreground) / 0.7)' }}>12 s/vB</span>
          <span className="font-body text-[6px]" style={{ color: 'hsl(var(--foreground) / 0.3)' }}>fee rate</span>
        </div>
        <div className="text-center">
          <span className="font-body font-semibold text-[10px] block" style={{ color: 'hsl(var(--foreground) / 0.7)' }}>#892,014</span>
          <span className="font-body text-[6px]" style={{ color: 'hsl(var(--foreground) / 0.3)' }}>latest block</span>
        </div>
      </div>

      <h2
        className="font-pixel text-[11px] tracking-wider mb-3"
        style={{ color: 'hsl(var(--bitcoin))' }}
      >
        The numbers at a glance
      </h2>
      <p
        className="font-body text-[11px] leading-relaxed"
        style={{ color: 'hsl(var(--foreground) / 0.7)' }}
      >
        Tap the panel to expand mining stats
        and halving countdown. Tap 📖 in the top bar
        for the full Guidance view.
      </p>
    </div>
  );
}

export function useOnboarding() {
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    setNeedsOnboarding(localStorage.getItem(STORAGE_KEY) !== 'true');
  }, []);

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setNeedsOnboarding(true);
  };

  const complete = () => setNeedsOnboarding(false);

  return { needsOnboarding, reset, complete };
}
